import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, where } from "firebase/firestore";
import * as XLSX from "xlsx";
import db from "../Firebase";

const parseDate = (val) => {
    if (!val) return null;
    if (typeof val?.toDate === "function") return val.toDate();
    if (val instanceof Date) return val;
    if (typeof val?.seconds === "number") return new Date(val.seconds * 1000);
    const d = new Date(val);
    return isNaN(d) ? null : d;
};

const fmtDate = (val) => {
    const d = parseDate(val);
    if (!d) return val ? String(val) : "-";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtNum = (val) => (val === null || val === undefined || val === "") ? "-" : Number(val).toFixed(2);
const cleanText = (val) => (val === null || val === undefined || val === "") ? "-" : String(val);
const cleanPhone = (val) => {
    if (!val) return "-";
    const digits = String(val).replace(/\D/g, "");
    return digits.length > 10 ? digits.slice(-10) : digits || "-";
};

export default function InvoiceData() {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [itemsModal, setItemsModal] = useState(null);
    const [userNameMap, setUserNameMap] = useState({});

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                const snap = await getDocs(collection(db, "nv_invoice_data"));
                const data = snap.docs.map(docSnap => ({ _id: docSnap.id, ...docSnap.data() }));
                data.sort((a, b) => {
                    const da = parseDate(a.created_at), db2 = parseDate(b.created_at);
                    return (db2?.getTime() || 0) - (da?.getTime() || 0);
                });
                setInvoices(data);

                // fetch full_name from personal_details by phone_no
                const phones = [...new Set(data.map(d => d.phone_no).filter(Boolean))];
                const map = {};
                for (let i = 0; i < phones.length; i += 10) {
                    const chunk = phones.slice(i, i + 10);
                    const pSnap = await getDocs(query(collection(db, "personal_details"), where("phone_no", "in", chunk)));
                    pSnap.docs.forEach(d => {
                        const pd = d.data();
                        if (pd.phone_no) map[pd.phone_no] = pd.full_name || pd.username || "";
                    });
                }
                setUserNameMap(map);
            } catch (err) {
                console.error("InvoiceData fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().replace(/\D/g, "");
        if (!q) return invoices;
        return invoices.filter(inv =>
            (inv.phone_no || "").includes(q) ||
            (inv.customer_phone || "").includes(q) ||
            (inv.business_phone || "").includes(q)
        );
    }, [invoices, search]);

    const handleExport = () => {
        const rows = [];
        filtered.forEach((inv, i) => {
            const base = {
                "#": i + 1,
                "Invoice No": cleanText(inv.invoice_number),
                "Invoice Date": cleanText(inv.invoice_date),
                "Generated By (Phone)": cleanText(inv.phone_no),
                "App User Name": cleanText(userNameMap[inv.phone_no]),
                "Customer Name": cleanText(inv.customer_name),
                "Customer Phone": cleanPhone(inv.customer_phone),
                "Business Name": cleanText(inv.business_name),
                "Business Phone": cleanText(inv.business_phone),
                "Business Address": cleanText(inv.business_address),
                "Business GST": cleanText(inv.business_gst),
                "Subtotal": fmtNum(inv.subtotal),
                "Discount %": fmtNum(inv.discount_percent),
                "Discount Amt": fmtNum(inv.discount_amount),
                "Tax %": fmtNum(inv.tax_percent),
                "Tax Amt": fmtNum(inv.tax_amount),
                "Shipping": fmtNum(inv.shipping),
                "Grand Total": fmtNum(inv.grand_total),
                "Payment Method": cleanText(inv.payment_method),
                "Notes": cleanText(inv.notes),
                "Created At": fmtDate(inv.created_at),
                "Item #": "",
                "Item Name": "",
                "Item Description": "",
                "Item Qty": "",
                "Item Unit Price": "",
                "Item Total": "",
            };

            if (Array.isArray(inv.items) && inv.items.length > 0) {
                inv.items.forEach((item, j) => {
                    rows.push({
                        ...base,
                        "#": j === 0 ? i + 1 : "",
                        "Item #": j + 1,
                        "Item Name": cleanText(item.name),
                        "Item Description": cleanText(item.description),
                        "Item Qty": item.qty ?? "-",
                        "Item Unit Price": fmtNum(item.unit_price),
                        "Item Total": fmtNum(item.total),
                    });
                });
            } else {
                rows.push(base);
            }
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Invoices");
        XLSX.writeFile(wb, "nv_invoice_data.xlsx");
    };

    return (
        <>
            <div className="container-fluid py-4">

                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h3 className="mb-1">Invoice Data</h3>
                        <div className="text-muted" style={{ fontSize: "13px" }}>
                            nv_invoice_data · {invoices.length} total records
                        </div>
                    </div>
                    <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
                </div>

                {/* SEARCH + EXPORT */}
                <div className="card mb-3">
                    <div className="card-body py-3 px-4">
                        <div className="d-flex gap-3 align-items-center flex-wrap">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by phone number…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ maxWidth: "320px" }}
                            />
                            {search && (
                                <span className="text-muted" style={{ fontSize: "13px" }}>
                                    {filtered.length} result{filtered.length !== 1 ? "s" : ""}
                                </span>
                            )}
                            <button
                                className="btn btn-success ms-auto"
                                onClick={handleExport}
                                disabled={loading || filtered.length === 0}
                                style={{ minWidth: "130px", fontWeight: 700 }}
                            >
                                Export Excel
                            </button>
                        </div>
                    </div>
                </div>

                {/* TABLE */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" />
                        <div className="mt-2 text-muted">Loading invoices…</div>
                    </div>
                ) : (
                    <div className="card">
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Invoice No</th>
                                        <th>Invoice Date</th>
                                        <th>Generated By</th>
                                        <th>App User Name</th>
                                        <th>Customer Name</th>
                                        <th>Customer Phone</th>
                                        <th>Business Name</th>
                                        <th>Business GST</th>
                                        <th className="text-end">Subtotal</th>
                                        <th className="text-end">Discount</th>
                                        <th className="text-end">Tax</th>
                                        <th className="text-end">Shipping</th>
                                        <th className="text-end">Grand Total</th>
                                        <th>Payment</th>
                                        <th className="text-center">Items</th>
                                        <th>Created At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.length === 0 ? (
                                        <tr>
                                            <td colSpan="17" className="text-center py-4 text-muted">
                                                {search ? "No invoices match this phone number" : "No invoices found"}
                                            </td>
                                        </tr>
                                    ) : filtered.map((inv, i) => (
                                        <tr key={inv._id}>
                                            <td>{i + 1}</td>
                                            <td style={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                                                {cleanText(inv.invoice_number)}
                                            </td>
                                            <td style={{ whiteSpace: "nowrap" }}>{cleanText(inv.invoice_date)}</td>
                                            <td>{cleanText(inv.phone_no)}</td>
                                            <td>{cleanText(userNameMap[inv.phone_no])}</td>
                                            <td>{cleanText(inv.customer_name)}</td>
                                            <td>{cleanPhone(inv.customer_phone || inv.phone_no)}</td>
                                            <td>{cleanText(inv.business_name)}</td>
                                            <td style={{ fontSize: "11px" }}>{cleanText(inv.business_gst)}</td>
                                            <td className="text-end">₹{fmtNum(inv.subtotal)}</td>
                                            <td className="text-end">
                                                {inv.discount_percent > 0 ? (
                                                    <span>
                                                        {fmtNum(inv.discount_percent)}%
                                                        <br />
                                                        <small className="text-danger">-₹{fmtNum(inv.discount_amount)}</small>
                                                    </span>
                                                ) : "-"}
                                            </td>
                                            <td className="text-end">
                                                {inv.tax_percent > 0 ? (
                                                    <span>
                                                        {fmtNum(inv.tax_percent)}%
                                                        <br />
                                                        <small className="text-muted">₹{fmtNum(inv.tax_amount)}</small>
                                                    </span>
                                                ) : "-"}
                                            </td>
                                            <td className="text-end">
                                                {inv.shipping > 0 ? `₹${fmtNum(inv.shipping)}` : "-"}
                                            </td>
                                            <td className="text-end fw-bold" style={{ color: "#16a34a" }}>
                                                ₹{fmtNum(inv.grand_total)}
                                            </td>
                                            <td>
                                                <span className="badge bg-secondary">
                                                    {cleanText(inv.payment_method)}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                {Array.isArray(inv.items) && inv.items.length > 0 ? (
                                                    <span
                                                        className="badge bg-primary"
                                                        style={{ cursor: "pointer", padding: "5px 10px" }}
                                                        onClick={() => setItemsModal({ inv, items: inv.items })}
                                                    >
                                                        {inv.items.length} item{inv.items.length > 1 ? "s" : ""}
                                                    </span>
                                                ) : "-"}
                                            </td>
                                            <td style={{ whiteSpace: "nowrap" }}>{fmtDate(inv.created_at)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* ITEMS MODAL */}
            {itemsModal && (
                <div
                    onClick={() => setItemsModal(null)}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                        zIndex: 9999, display: "flex", alignItems: "center",
                        justifyContent: "center", padding: "20px",
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "#fff", borderRadius: "10px", width: "100%",
                            maxWidth: "700px", maxHeight: "80vh",
                            display: "flex", flexDirection: "column",
                            boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-start px-4 py-3 border-bottom">
                            <div>
                                <div className="fw-bold" style={{ fontSize: "15px" }}>
                                    {cleanText(itemsModal.inv.invoice_number)} — Items
                                </div>
                                <div className="text-muted" style={{ fontSize: "12px" }}>
                                    {cleanText(itemsModal.inv.customer_name)} · {cleanText(itemsModal.inv.customer_phone || itemsModal.inv.phone_no)}
                                </div>
                            </div>
                            <button className="btn-close" onClick={() => setItemsModal(null)} />
                        </div>
                        <div style={{ overflowY: "auto", padding: "16px" }}>
                            <table className="table table-sm table-bordered align-middle mb-3" style={{ fontSize: "13px" }}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Description</th>
                                        <th className="text-center">Qty</th>
                                        <th className="text-end">Unit Price</th>
                                        <th className="text-end">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {itemsModal.items.map((item, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td>{cleanText(item.name)}</td>
                                            <td>{cleanText(item.description)}</td>
                                            <td className="text-center">{item.qty ?? "-"}</td>
                                            <td className="text-end">₹{fmtNum(item.unit_price)}</td>
                                            <td className="text-end fw-bold">₹{fmtNum(item.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="d-flex flex-column align-items-end gap-1" style={{ fontSize: "13px" }}>
                                <div>Subtotal: <strong>₹{fmtNum(itemsModal.inv.subtotal)}</strong></div>
                                {itemsModal.inv.discount_amount > 0 && (
                                    <div className="text-danger">Discount ({fmtNum(itemsModal.inv.discount_percent)}%): -₹{fmtNum(itemsModal.inv.discount_amount)}</div>
                                )}
                                {itemsModal.inv.tax_amount > 0 && (
                                    <div className="text-muted">Tax ({fmtNum(itemsModal.inv.tax_percent)}%): ₹{fmtNum(itemsModal.inv.tax_amount)}</div>
                                )}
                                {itemsModal.inv.shipping > 0 && (
                                    <div className="text-muted">Shipping: ₹{fmtNum(itemsModal.inv.shipping)}</div>
                                )}
                                <div style={{ fontSize: "15px", fontWeight: 800, color: "#16a34a", borderTop: "2px solid #e2e8f0", paddingTop: "6px", marginTop: "4px" }}>
                                    Grand Total: ₹{fmtNum(itemsModal.inv.grand_total)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
